/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Turns one press of Pay into a body for POST /arcee/payments/checkout/.
 *
 * The sibling of buildRegistrationBatch.js, and deliberately NOT a second copy
 * of it: both call the same `seatOperations` for the rows a seat becomes. What
 * differs is only when those rows get written. The free path sends them to be
 * written now; this one parks them inside the payment, and the gateway's
 * webhook writes them once the money has actually moved.
 *
 * WHY PAYMENT-FIRST. `event_activity.max_participants` is a real cap, so a
 * registration written before payment would hold a seat for somebody who never
 * pays. Nothing exists here until the webhook creates it, so an abandoned
 * checkout leaves nothing to clean up and no seat held.
 *
 * ONE CART, EVEN WHEN SOME SEATS ARE FREE. A mixed press sends every seat,
 * with the free ones priced at zero. Only the priced seats add to the amount,
 * so the runner is still charged only for what costs money — but all of them
 * land in one batch on one webhook, so nobody ends up half-registered with the
 * paid half missing.
 *
 * REFS ARE NAMESPACED PER SEAT. Server-side, every line item's operations are
 * concatenated into ONE execute_batch, so `r0` from two different seats would
 * collide and the second pass would point at the first seat's registration.
 * `s<index>r<index>` keeps them distinct across the whole payment.
 */

import envConfig from '../../../config/envConfig';
import {
  seatOperations, seatRef, REGISTRATION_TABLE, MAX_OPERATIONS, maxSeatsPerCheckout,
} from './buildRegistrationBatch';
import { CONFIRMED, HOLD, CANCELLED } from './registrationRules';

/**
 * Names the reserved row inside a confirm or release operation.
 *
 * The page cannot know the id of a row it has not written yet, and the server
 * cannot know what the row is for. This marker is where the two meet: the
 * server replaces it with the id it recorded when the place was reserved.
 *
 * It is also the only way these operations may name a row, and the server
 * refuses the checkout if they name one any other way. That is what makes it
 * safe for them to carry update rights at all — without it, a crafted payload
 * could cancel a stranger's entry the moment its own payment succeeded.
 */
export const HELD_ROW = '@held';

/** The ChargeableEntity code a seat is sold as. Must exist in Admin. */
export const REGISTRATION_ENTITY_CODE = 'event_registration';

/** A seat's price, as a number. Missing or unparseable reads as free. */
export function seatFee(seat) {
  const fee = seat?.activity?.fee_amount;
  if (fee === undefined || fee === null || fee === '') return 0;
  const numeric = Number(fee);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

/** Does this press of Continue involve money at all? */
export function isPaidCheckout(requests) {
  return (Array.isArray(requests) ? requests : []).some((seat) => seatFee(seat) > 0);
}

/** What the runner is about to be charged, across every seat. */
export function checkoutTotal(requests) {
  return (Array.isArray(requests) ? requests : [])
    .reduce((sum, seat) => sum + seatFee(seat), 0);
}

/**
 * The HELD form of one seat: the place is taken, nothing is confirmed.
 *
 * Written the instant Pay is pressed, which is the point — a seat that only
 * becomes a row when the webhook lands is invisible to everybody for the 20 to
 * 65 seconds in between, and that is long enough for two more people to be
 * charged for it.
 *
 * ONE ROW AND NO PASS. A pass is proof of entry to show at a gate, and nothing
 * has been paid for yet. It is minted by the confirm form below.
 */
export function holdForSeat(seat, options) {
  const holdRules = { ...options.rules, seatStatus: HOLD, mintsPass: false };
  const operations = seatOperations(seat, { ...options, rules: holdRules });
  const ref = seatRef(options.index, options.refPrefix);

  // Named because the server backfills this line item's instance id from the
  // batch's refs, and an unnamed row would be written and then left unlinked
  // to the payment holding it.
  return {
    ref,
    operations: operations.map((op, i) => (i === 0 ? { ...op, ref } : op)),
  };
}

/**
 * What the held seat BECOMES once the money arrives.
 *
 * Derived from the very same `seatOperations` as every other form, then
 * rewritten into its two real steps, so the columns on a confirmed entry
 * cannot drift from the ones a free entry writes:
 *
 *   1. the held row is updated in place -- same row, now CONFIRMED, so the
 *      place it was holding is the place it keeps;
 *   2. the pass is inserted, pointing at that row.
 *
 * Step 1 is an UPDATE, which is why it may name its target only as `@held`.
 */
export function confirmForSeat(seat, options) {
  const confirmRules = { ...options.rules, seatStatus: CONFIRMED };
  const built = seatOperations(seat, { ...options, rules: confirmRules });
  const ref = seatRef(options.index, options.refPrefix);

  const operations = [{
    table: REGISTRATION_TABLE,
    // Only what actually changes. Re-sending the whole row would rewrite
    // `registered_at` and `created_by_id`, which belong to the moment the
    // place was taken, not to the moment the payment cleared.
    data: { id: HELD_ROW, status: CONFIRMED },
  }];

  // Every row after the registration -- the pass, today -- pointed at the
  // registration by its ref while both were being inserted together. The
  // registration already exists now, so they point at it by id instead.
  built.slice(1).forEach((op) => {
    const data = { ...op.data };
    Object.keys(data).forEach((column) => {
      if (data[column] === `@${ref}`) data[column] = HELD_ROW;
    });
    operations.push({ ...op, data });
  });

  return { ref, operations };
}

/**
 * Giving the place back, when it will not be paid for.
 *
 * Used when the buyer starts again, when the payment fails, and when the hold
 * ages out. CANCELLED rather than deleted: the cap counts neither CANCELLED
 * nor PENDING, so the place is free again, and the row stays as a record that
 * somebody tried.
 */
export function releaseForSeat(seat, options) {
  return {
    ref: seatRef(options.index, options.refPrefix),
    operations: [{
      table: REGISTRATION_TABLE,
      data: { id: HELD_ROW, status: CANCELLED },
    }],
  };
}

/**
 * Builds the checkout body.
 *
 * `idempotencyKey` is required and must be the one held for THIS selection —
 * see the modal's key ref. Passing a fresh key per attempt opens a second
 * gateway order for the same press; passing a stale one after the cart changed
 * is refused by the server with 422 rather than charging the old cart.
 */
export function buildCheckoutPayload(requests, {
  eventId, rules, organizationId, applicationId, answers, answerMap,
  applicationUserId, userInfo, idempotencyKey, returnUrl, customer, currency,
  couponCode,
} = {}) {
  const seats = Array.isArray(requests) ? requests : [];

  if (!seats.length) {
    throw new Error('Nothing was chosen to register for.');
  }
  if (eventId === undefined || eventId === null || eventId === '') {
    throw new Error('Cannot register without the event this is for.');
  }
  if (!rules || !rules.canRegister) {
    throw new Error('Entry is not open for this event.');
  }
  if (!idempotencyKey) {
    // Refused rather than defaulted. A checkout with no key has no replay
    // protection at all, so a double-click charges twice — and silently
    // minting one here would hide the bug that the modal lost its key.
    throw new Error('This checkout could not be started safely. Please try again.');
  }

  let operationCount = 0;
  const items = seats.map((seat, index) => {
    const refPrefix = `s${index}r`;
    const seatOptions = {
      index, eventId, rules, answers, answerMap, applicationUserId, userInfo, refPrefix,
    };
    // THE HOLD IS WHAT CHECKOUT WRITES NOW, not the confirmed entry. The
    // confirmed form still exists -- it is `on_confirm` below -- but it is
    // applied when the money arrives, and by then the place is already this
    // buyer's and cannot be lost to anybody.
    const hold = holdForSeat(seat, seatOptions);
    const confirm = confirmForSeat(seat, seatOptions);
    const operations = hold.operations;
    // The server concatenates the operations it runs AT CHECKOUT, and
    // on_confirm runs later, in a batch of its own. Counted against the cap
    // separately for that reason -- whichever is larger decides how many seats
    // fit in one press, and the confirm batch is the bigger of the two
    // whenever a pass is minted.
    operationCount += Math.max(operations.length, confirm.operations.length);

    return {
      chargeable_entity_code: REGISTRATION_ENTITY_CODE,
      // The event, for fee/analytics resolution server-side. The activity
      // travels inside the operations, where the row that needs it lives.
      chargeable_entity_parent_id: eventId,
      // WHICH ROW THE PRICE HANGS OFF. The activity, not the event: a 5K and a
      // Half under one event are not the same price, so fee_amount lives on
      // event_activity. Deliberately separate from parent_id above, which is
      // the event and is used for fee/analytics scoping.
      //
      // The server re-reads the price from this row and treats `amount` below
      // as a claim to check rather than a figure to charge — so a tampered
      // amount is refused rather than believed.
      price_ref_id: seat?.activity?.id,
      amount: String(seatFee(seat)),
      record_payload: {
        // Which created row IS the thing sold, so the server can backfill this
        // line item's chargeable_entity_instance_id from the batch's refs.
        // The registration, never the pass: the pass is a byproduct of the
        // seat, not separately purchased.
        ref: seatRef(index, refPrefix),
        operations,
        // WHAT THIS SEAT BECOMES WHEN THE PAYMENT CLEARS.
        //
        // Its presence is also the switch that tells the server to reserve at
        // checkout at all: without it the server defers the whole write to the
        // webhook, which is exactly how this behaved before holds existed.
        on_confirm: confirm,
        // AND WHAT IT BECOMES IF IT IS NEVER PAID FOR. Applied when the buyer
        // starts again, when the payment fails, and by the job that sweeps up
        // holds nobody came back for. Without it a seat abandoned at the
        // gateway would stay held until somebody noticed.
        on_release: releaseForSeat(seat, seatOptions),
        // NO `on_full`, DELIBERATELY. A paid checkout used to declare a
        // waiting-list form here, so a buyer whose places ran out mid-payment
        // was quietly given a queue position instead. A place is either theirs
        // or it is not; a paying customer holding neither a place nor their
        // money is not an outcome this product offers.
        //
        // It is also close to unreachable now: the places are reserved before
        // the gateway is called, so a cart that does not fit is refused with
        // `insufficient_capacity` and nothing is charged. If money does somehow
        // arrive for a place that is gone, the server has no fallback declared
        // and therefore marks the payment as owing a refund -- see
        // PaymentsWebhookAPIView._fulfil_released_lines.
      },
    };
  });

  // The server concatenates every line's operations into one batch, so the
  // platform's own cap applies to their SUM, not to any single line.
  if (operationCount > MAX_OPERATIONS) {
    // Same sentence as the free path, for the same reason -- see
    // buildRegistrationBatch. A paid cart reaching here has not been charged
    // yet, so this is a refusal the visitor can still act on.
    const allowed = maxSeatsPerCheckout(Boolean(rules && rules.mintsPass));
    const over = seats.length - allowed;
    throw new Error(
      `You can book up to ${allowed} seats in one go, and ${seats.length} were `
      + `chosen. Please remove ${over} ${over === 1 ? 'seat' : 'seats'} — you `
      + 'can book the rest straight after this booking.'
    );
  }

  return {
    organization_id: organizationId ?? envConfig.organizationId,
    application_id: applicationId ?? envConfig.applicationId,
    idempotency_key: idempotencyKey,
    return_url: returnUrl,
    ...(currency ? { currency } : {}),
    // Contact details for the gateway. Identity (who is paying) is taken from
    // the session server-side and is deliberately not sent from here.
    customer: customer || {},
    // Omitted entirely when no code was applied, so a checkout without a
    // coupon sends byte-for-byte the body it always sent.
    ...(couponCode ? { coupon_code: couponCode } : {}),
    items,
  };
}
